import React, { useMemo, useState } from 'react';
import { FlatList, RefreshControl, ScrollView, TouchableOpacity, View } from 'react-native';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Icon from '../../../../components/IcomoonIcon';
import Header from '../../../../navigation/Header';
import api from '../../../../utils/api';

import AccessAuthorizationDetailModal from './AccessAuthorizationDetailModal';

dayjs.locale('es');


const STATUS_COLOR: Record<string, string> = {
  pending: 'text-amber-600',
  approved: 'text-emerald-600',
  rejected: 'text-red-500',
  suspended: 'text-red-500',
  cancelled: 'text-gray-400',
  expired: 'text-gray-400',
};


/**
 * `status` permite cambiar la etiqueta: en anteriores "Vencida" significa que nadie la contestó,
 * mientras que la API llama `expired` a la que se aprobó y ya pasó.
 */
export function AccessAuthorizationRow({ auth, onPress, status }: { auth: any; onPress?: () => void; status?: { label: string; color: string } }) {
  const start = dayjs(auth.start_date);
  const end = auth.end_date ? dayjs(auth.end_date) : null;
  const range = !end || start.isSame(end, 'day')
    ? start.format('ddd D MMM')
    : `${start.format('D MMM')} - ${end.format('D MMM')}`;

  const statusLabel = status?.label ?? auth.status_str;
  const statusColor = status?.color ?? STATUS_COLOR[auth.display_status ?? auth.status];

  return (
    <TouchableOpacity style={tw`row justify-between border-b border-gray-200 px-4 py-3`} onPress={onPress}>
      <View style={tw`flex-1 pr-3`}>
        {/* La descripción va sola: en el mismo renglón que el estado se cortaba */}
        <Text style={tw`text-gray-500 font-500`} numberOfLines={2}>{[auth.service_type, auth.description].filter(Boolean).join(' · ')}</Text>
        <View style={tw`row justify-between items-center mt-1`}>
          <View style={tw`row flex-1`}>
            <Text style={tw`text-gray-400 mr-3`}>{range}</Text>
            <Text style={tw`text-gray-400`}>{auth.access_type_str}</Text>
          </View>
          <Text style={tw.style(`text-sm font-500 ml-2`, statusColor)}>{statusLabel}</Text>
        </View>
      </View>

      <Icon name='chevron-right' color={tw.color('secondary')} />
    </TouchableOpacity>
  );
}


const PER_PAGE = 30;

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// Dentro de period=past el `status` de la API separa: expired = se aprobó y ya pasó, pending = nadie la contestó
const FILTERS = [
  { value: 'all', label: 'Todas', status: undefined, noun: 'en total' },
  { value: 'approved', label: 'Aprobadas', status: 'expired', noun: 'aprobadas' },
  { value: 'unanswered', label: 'Vencidas', status: 'pending', noun: 'vencidas' },
  { value: 'rejected', label: 'Rechazadas', status: 'rejected', noun: 'rechazadas' },
];

// Etiquetas desde el punto de vista del residente, ya con la fecha pasada
const PAST_STATUS: Record<string, { label: string; color: string }> = {
  approved: { label: 'Aprobada', color: 'text-emerald-600' },
  pending: { label: 'Vencida', color: 'text-gray-400' },
  rejected: { label: 'Rechazada', color: 'text-red-500' },
  cancelled: { label: 'Cancelada', color: 'text-gray-400' },
  suspended: { label: 'Suspendida', color: 'text-red-500' },
};


function FilterChips({ value, onChange }) {
  // El View fija el alto: el ScrollView horizontal solo se encogía frente a la lista y cortaba las píldoras
  return (
    <View style={tw`shrink-0 py-3`}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={tw`px-2 gap-x-2 items-center`}>
        {FILTERS.map(filter => {
          const selected = filter.value === value;
          return (
            <TouchableOpacity
              key={filter.value}
              onPress={() => onChange(filter.value)}
              style={tw.style(`rounded-full px-4 py-1.5 border`, selected ? `bg-[#11554F] border-[#11554F]` : `bg-white border-gray-200`)}
            >
              <Text style={tw.style(selected ? `text-white font-500` : `text-gray-500`)}>{filter.label}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
}


/** Autorizaciones anteriores: aprobadas que ya pasaron, vencidas, rechazadas y canceladas. Las vigentes se quedan en Accesos. */
export default function AccessAuthorizationsScreen({ navigation, route }) {
  const { communityId } = route.params;
  const [shown, setShown] = useState<any>(null);
  const [filterValue, setFilterValue] = useState('all');
  const filter = FILTERS.find(f => f.value === filterValue) ?? FILTERS[0];

  const query = useInfiniteQuery<any>({
    queryKey: ['resident.access-authorizations', communityId, 'mine', 'past', filter.value],
    queryFn: ({ pageParam }) =>
      api
        .get(`/communities/${communityId}/access-authorizations`, {
          params: { mine: 1, period: 'past', status: filter.status, page: pageParam, per_page: PER_PAGE },
        })
        .then(r => r.data),
    initialPageParam: 1,
    getNextPageParam: (last: any) => last.current_page < last.last_page ? last.current_page + 1 : undefined,
    placeholderData: keepPreviousData,
  });

  const total = query.data?.pages[0]?.total ?? 0;

  // Por mes de la fecha de acceso, lo más reciente arriba (igual que Comunicados)
  const monthGroups = useMemo(() => {
    const items = query.data?.pages.flatMap((page: any) => page.data) ?? [];
    const groups: Record<string, { key: string; label: string; items: any[] }> = {};
    items.forEach((auth: any) => {
      const d = dayjs(auth.start_date);
      const key = d.format('YYYY-MM');
      if (!groups[key]) {
        groups[key] = { key, label: `${MONTHS[d.month()]} ${d.year()}`, items: [] };
      }
      groups[key].items.push(auth);
    });
    return Object.values(groups)
      .sort((a, b) => (a.key < b.key ? 1 : -1))
      .map(g => ({ ...g, items: g.items.sort((x, y) => (x.start_date < y.start_date ? 1 : -1)) }));
  }, [query.data]);

  return (
    <View style={tw`flex-1 bg-[#F3F2F3]`}>
      <Header showBackButton backButtonColor='white' style={tw`bg-[#11554F] rounded-b-0 mb-0`}>
        <View>
          <Text style={tw`text-lg text-white font-600`}>Autorizaciones anteriores</Text>
          {query.isLoading
            ? <Text style={tw`text-white/50`}>Cargando...</Text>
            : <Text style={tw`text-white/80`}>{total} {filter.noun}</Text>}
        </View>
      </Header>

      <FilterChips value={filterValue} onChange={setFilterValue} />

      <FlatList
        data={monthGroups}
        keyExtractor={group => group.key}
        contentContainerStyle={tw`px-2 pb-20`}
        renderItem={({ item: group }) => (
          <View style={tw`mb-6`}>
            <View style={tw`bg-gray-200 mb-4 self-center px-6 py-0.5 rounded-full border border-gray-300`}>
              <Text style={tw`text-gray-400`}>{group.label}</Text>
            </View>

            <View style={tw`bg-white border border-gray-200 rounded-2xl overflow-hidden`}>
              {group.items.map((auth: any) => (
                <AccessAuthorizationRow key={auth.uuid} auth={auth} status={PAST_STATUS[auth.status]} onPress={() => setShown(auth)} />
              ))}
            </View>
          </View>
        )}
        refreshControl={<RefreshControl onRefresh={query.refetch} refreshing={query.isRefetching && !query.isFetchingNextPage} />}
        onEndReached={() => { if (query.hasNextPage && !query.isFetchingNextPage) query.fetchNextPage(); }}
        onEndReachedThreshold={0.4}
        ListEmptyComponent={
          !query.isLoading
            ? <Text style={tw`text-gray-400 text-center mt-20`}>
                {filter.value === 'all' ? 'No tienes autorizaciones anteriores' : `No tienes autorizaciones ${filter.noun}`}
              </Text>
            : null
        }
      />

      <AccessAuthorizationDetailModal
        visible={!!shown}
        authorization={shown}
        communityId={communityId}
        onRequestClose={() => setShown(null)}
        onChanged={query.refetch}
      />
    </View>
  );
}
